<template>
  <div class="c_hero_banner" @mouseenter="pauseAutoplay()" @mouseleave="setAutoplay()">
    <!-- [START] SLIDE -->
    <section
      class="c_hero_banner__slide"
      v-for="(slide, key) in slideData"
      :key="key"
      :class="[{ active: key === currentSlideId }, desktopBannerAspectSizingClass, mobileBannerAspectSizingClass]"
    >
      <div
        class="sizing-box"
        :class="[desktopBannerSizingClass, mobileBannerSizingClass, slide.content.stackTextImageMobile]"
        :style="
          '--mobile-banner-height:' +
          bannerSize.mobileBannerHeight +
          '; --desktop-banner-height:' +
          bannerSize.desktopBannerHeight +
          ';'
        "
      >
        <div
          class="c_hero_banner__background"
          :class="[slide.content.stackTextImageMobile]"
          :style="'--mobile-image-order:' + slide.content.mobileImageOrder + ';'"
        >
          <!--[START] Desktop Variant BG (default)-->
          <div class="c_hero_banner__background__overlay dark" :style="'--opacity:' + slide.overlayOpacity"></div>
          <div
            v-if="slide.desktopBgImageUrl"
            class="c_hero_banner__background__visual desktop"
            :style="'background-image: url(' + slide.desktopBgImageUrl + ')'"
          ></div>
          <div
            v-if="slide.desktopBgVideoUrl && ['lg'].includes($mq)"
            class="c_hero_banner__background__visual desktop"
            :style="'background-image: url(' + slide.desktopBgImageUrl + ')'"
          >
            <video
              v-if="slide.desktopBgVideoUrl"
              :id="'slideVideo' + key"
              :ref="'slideVideo' + key"
              class="c_hero_banner__background__visual__video"
              autoplay
              playsinline
              muted
              :loop="slide.content.loopBackgroundVideo"
              :poster="slide.desktopBgImageUrl"
              @ended="onVideoEnd(key)"
            >
              <source :src="slide.desktopBgVideoUrl" type="video/mp4" />
            </video>
          </div>
          <div v-if="['sm', 'md'].includes($mq)">
            <!--[START] Mobile Variant BG-->
            <MediaImage
              background
              v-if="slide.mobileBgImageSrcset"
              :sources="[slide.mobileBgImageSrcset]"
              class="c_hero_banner__background__visual mobile"
            />
            <!-- BG Video for Mobile -->
            <!-- Pause button for mobile to avoid overlap with desktop button -->
            <div v-if="slide.mobileBgVideoUrl" class="pause-resume pause-resume-mobile" @click="toggleVideoState(key)">
              <IconSVG height="8" width="8" :handle="pauseResumeIcon[key]" aria-hidden="true" />
            </div>
            <video
              v-if="slide.mobileBgVideoUrl"
              :id="'slideVideo' + key"
              :ref="'slideVideo' + key"
              class="c_hero_banner__background__visual mobile"
              autoplay
              playsinline
              muted
              :loop="slide.content.loopBackgroundVideoMobile"
              :poster="slide.mobileBgImageUrl"
              @ended="onVideoEnd(key)"
            >
              <source :src="slide.mobileBgVideoUrl" type="video/mp4" />
            </video>
          </div>
        </div>
        <div
          class="c_hero_banner__content"
          :class="[slide.content.stackTextImageMobile]"
          v-if="slide.content.stackTextImageMobile !== 'stack-items'"
          :style="
            '--desktop-content-position:' +
            slide.content.contentPositionDesktop +
            '; --mobile-content-position:' +
            slide.content.contentPositionMobile +
            '; --mobile-text-order:' +
            slide.content.mobileTextOrder +
            '; --desktop-text-box-width:' +
            slide.content.desktopTextBoxWidth +
            '; --desktop-text-align:' +
            slide.content.desktopTextAlign +
            '; --mobile-text-align:' +
            slide.content.mobileTextAlign +
            ';'
          "
        >
          <section class="content_wrapper">
            <div class="content-container-box">
              <div
                class="content-box"
                :class="[slideContentBoxThemeClass, slide.content.stackTextImageMobile, textShadowThemeClass]"
                :style="
                  '--shadow-offset:' +
                  slide.content.textShadowOffset +
                  'px;' +
                  '--shadow-blur:' +
                  slide.content.textShadowBlurStrength +
                  'px;'
                "
              >
                <p v-if="slide.content.eyebrow" v-html="slide.content.eyebrow" class="c_lp_text__eyebrow"></p>
                <!-- headingTag is a lightswitch in the CMS which is a boolean by default -->
                <element
                  :is="slide.content.headingTag ? 'h2' : 'h1'"
                  v-html="slide.content.header"
                  v-if="slide.content.header"
                  class="c_lp_text__subheader c_text--graphik_bold"
                  :class="[slide.content.headingTag === true ? 'c_header--7' : 'c_header--10']"
                >
                </element>
                <div
                  v-if="slide.content.description"
                  class="c_lp_text__text c_text c_text--graphik_regular"
                  v-html="slide.content.description"
                ></div>
                <div
                  class="cta_container"
                  :style="
                    '--desktop-justify-align:' +
                    slide.content.desktopFlexButtonAlign +
                    '; --mobile-justify-align:' +
                    slide.content.mobileFlexButtonAlign +
                    ';'
                  "
                >
                  <span v-if="slide.content.primaryBtn.url && slide.content.primaryBtn.label">
                    <a
                      v-if="
                        slide.content.primaryBtn.url &&
                        slide.content.primaryBtn.label &&
                        !slide.content.secondaryBtn.label
                      "
                      :class="['c_button_2022', 'c_button_2022--primary', slide.content.primaryBtn.themeClass]"
                      :href="slide.content.primaryBtn.url"
                      :aria-label="slide.content.primaryBtn.ariaLabel"
                      :target="slide.content.primaryBtn.target"
                      >{{ slide.content.primaryBtn.label }}</a
                    >
                    <a
                      v-else-if="slide.content.primaryBtn.url && slide.content.primaryBtn.label"
                      :class="['c_button_2022', 'c_button_2022--primary', slide.content.primaryBtn.themeClass]"
                      :href="slide.content.primaryBtn.url"
                      :aria-label="slide.content.primaryBtn.ariaLabel"
                      :target="slide.content.primaryBtn.target"
                      >{{ slide.content.primaryBtn.label }}</a
                    >
                  </span>

                  <span v-if="slide.content.secondaryBtn.url && slide.content.secondaryBtn.label">
                    <a
                      v-if="slide.content.secondaryBtn.url && slide.content.secondaryBtn.label"
                      :class="['c_button_2022', 'c_button_2022__secondary', slide.content.secondaryBtn.themeClass]"
                      :href="slide.content.secondaryBtn.url"
                      :aria-label="slide.content.secondaryBtn.ariaLabel"
                      :target="slide.content.secondaryBtn.target"
                      >{{ slide.content.secondaryBtn.label }}</a
                    >
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>
        <div
          class="c_hero_banner__content"
          :class="[slide.content.stackTextImageMobile, `${slideContentBoxThemeClass}--mobile`]"
          v-if="mobileBannerSizingClass === 'mobile-set-height' && slide.content.stackTextImageMobile === 'stack-items'"
          :style="
            '--desktop-content-position:' +
            slide.content.contentPositionDesktop +
            '; --mobile-content-position:' +
            slide.content.contentPositionMobile +
            '; --mobile-text-order:' +
            slide.content.mobileTextOrder +
            '; --desktop-text-box-width:' +
            slide.content.desktopTextBoxWidth +
            '; --desktop-text-align:' +
            slide.content.desktopTextAlign +
            '; --mobile-text-align:' +
            slide.content.mobileTextAlign +
            ';'
          "
        >
          <section class="content_wrapper">
            <div class="content-container-box">
              <div
                class="content-box"
                :class="[slideContentBoxThemeClass, slide.content.stackTextImageMobile, textShadowThemeClass]"
                :style="
                  '--shadow-offset:' +
                  slide.content.textShadowOffset +
                  'px;' +
                  '--shadow-blur:' +
                  slide.content.textShadowBlurStrength +
                  'px;'
                "
              >
                <p v-if="slide.content.eyebrow" v-html="slide.content.eyebrow" class="c_lp_text__eyebrow"></p>
                <element
                  :is="slide.content.headingTag ? 'h2' : 'h1'"
                  v-html="slide.content.header"
                  v-if="slide.content.header"
                  class="c_lp_text__subheader c_text--graphik_bold"
                  :class="[slide.content.headingTag === true ? 'c_header--2' : 'c_header--10']"
                >
                </element>
                <div
                  v-if="slide.content.description"
                  class="c_lp_text__text c_text c_text--graphik_regular"
                  v-html="slide.content.description"
                ></div>
                <div
                  class="cta_container"
                  :style="
                    '--desktop-justify-align:' +
                    slide.content.desktopFlexButtonAlign +
                    '; --mobile-justify-align:' +
                    slide.content.mobileFlexButtonAlign +
                    ';'
                  "
                >
                  <span v-if="slide.content.primaryBtn.url || slide.content.secondaryBtn.url">
                    <a
                      v-if="
                        slide.content.primaryBtn.url &&
                        slide.content.primaryBtn.label &&
                        !slide.content.secondaryBtn.label
                      "
                      :class="['c_button_2022', 'c_button_2022--primary', slide.content.primaryBtn.themeClass]"
                      :href="slide.content.primaryBtn.url"
                      :aria-label="slide.content.primaryBtn.ariaLabel"
                      :target="slide.content.primaryBtn.target"
                      >{{ slide.content.primaryBtn.label }}</a
                    >
                    <a
                      v-else-if="slide.content.primaryBtn.url && slide.content.primaryBtn.label"
                      :class="['c_button_2022', 'c_button_2022--primary', slide.content.primaryBtn.themeClass]"
                      :href="slide.content.primaryBtn.url"
                      :aria-label="slide.content.primaryBtn.ariaLabel"
                      :target="slide.content.primaryBtn.target"
                      >{{ slide.content.primaryBtn.label }}</a
                    >
                  </span>
                  <span v-if="slide.content.secondaryBtn.url">
                    <a
                      v-if="slide.content.secondaryBtn.url && slide.content.secondaryBtn.label"
                      :class="['c_button_2022', 'c_button_2022__secondary', slide.content.secondaryBtn.themeClass]"
                      :href="slide.content.secondaryBtn.url"
                      :aria-label="slide.content.secondaryBtn.ariaLabel"
                      :target="slide.content.secondaryBtn.target"
                      >{{ slide.content.secondaryBtn.label }}</a
                    >
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
    <div
      class="aspect_ratio_stack_set"
      v-if="
        mobileBannerSizingClass !== 'mobile-set-height' &&
        slideData[currentSlideId].content.stackTextImageMobile === 'stack-items'
      "
    >
      <div
        class="c_hero_banner__content"
        v-for="(slide, key) in slideData"
        :key="key"
        :class="[{ active: key === currentSlideId }, slide.content.stackTextImageMobile]"
        :style="
          '--desktop-content-position:' +
          slide.content.contentPositionDesktop +
          '; --mobile-content-position:' +
          slide.content.contentPositionMobile +
          '; --mobile-text-order:' +
          slide.content.mobileTextOrder +
          '; --desktop-text-box-width:' +
          slide.content.desktopTextBoxWidth +
          '; --desktop-text-align:' +
          slide.content.desktopTextAlign +
          '; --mobile-text-align:' +
          slide.content.mobileTextAlign +
          ';'
        "
      >
        <section class="content_wrapper">
          <div class="content-container-box">
            <div
              class="content-box"
              :class="[slideContentBoxThemeClass, slide.content.stackTextImageMobile, textShadowThemeClass]"
              :style="
                '--shadow-offset:' +
                slide.content.textShadowOffset +
                'px;' +
                '--shadow-blur:' +
                slide.content.textShadowBlurStrength +
                'px;'
              "
            >
              <p v-if="slide.content.eyebrow" v-html="slide.content.eyebrow" class="c_lp_text__eyebrow"></p>
              <h1
                v-if="slide.content.header && slide.content.headingTag === false"
                v-html="slide.content.header"
                class="c_lp_text__subheader c_header--10 c_text--graphik_bold"
              ></h1>
              <h2
                v-else-if="slide.content.header && slide.content.headingTag === true"
                v-html="slide.content.header"
                class="c_lp_text__subheader c_header--10 c_text--graphik_bold"
              ></h2>
              <div
                v-if="slide.content.description"
                class="c_lp_text__text c_text c_text--graphik_regular"
                v-html="slide.content.description"
              ></div>
              <div
                class="cta_container"
                :style="
                  '--desktop-justify-align:' +
                  slide.content.desktopFlexButtonAlign +
                  '; --mobile-justify-align:' +
                  slide.content.mobileFlexButtonAlign +
                  ';'
                "
              >
                <span v-if="slide.content.primaryBtn.url">
                  <a
                    v-if="
                      slide.content.primaryBtn.url &&
                      slide.content.primaryBtn.label &&
                      !slide.content.secondaryBtn.label
                    "
                    :class="['c_button_2022', 'c_button_2022--primary', slide.content.primaryBtn.themeClass]"
                    :href="slide.content.primaryBtn.url"
                    :aria-label="slide.content.primaryBtn.ariaLabel"
                    :target="slide.content.primaryBtn.target"
                    >{{ slide.content.primaryBtn.label }}</a
                  >
                  <a
                    v-else-if="slide.content.primaryBtn.url && slide.content.primaryBtn.label"
                    :class="['c_button_2022', 'c_button_2022--primary', slide.content.primaryBtn.themeClass]"
                    :href="slide.content.primaryBtn.url"
                    :aria-label="slide.content.primaryBtn.ariaLabel"
                    :target="slide.content.primaryBtn.target"
                    >{{ slide.content.primaryBtn.label }}</a
                  >
                </span>
                <span v-if="slide.content.secondaryBtn.url">
                  <a
                    v-if="slide.content.secondaryBtn.url && slide.content.secondaryBtn.label"
                    :class="['c_button_2022', 'c_button_2022__secondary', slide.content.secondaryBtn.themeClass]"
                    :href="slide.content.secondaryBtn.url"
                    :aria-label="slide.content.secondaryBtn.ariaLabel"
                    :target="slide.content.secondaryBtn.target"
                    >{{ slide.content.secondaryBtn.label }}</a
                  >
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
    <!-- [END] SLIDE -->
    <!-- ========================================================================================= -->
    <!--[START] Hero Banner Indicator -->
    <div v-if="slideData.length > 1" class="c_hero_banner__indicator container">
      <!-- [START] Indicator (icon_indicator optional)-->
      <div class="c_hero_banner__indicator__menu__container" :class="[indicatorClass]">
        <nav class="c_hero_banner__indicator__menu">
          <button
            unstyle
            reset
            class="arrow-nav-btn arrow-nav-btn--mobile left-btn c_button--reset"
            @click="goToPreviousSlide({ userInitiated: true })"
            v-if="slideData.length > 1"
          >
            <IconSVG class="arrow-icon" handle="arrow_left" width="6" height="12" />
          </button>
          <button
            unstyle
            reset
            class="indicator_btn c_button--reset"
            :id="`indicator_btn--${key}`"
            v-for="(slide, key) in slideData"
            :key="key"
            :class="[{ active: key === currentSlideId }]"
            @click="goToSlide(key)"
          >
            <img
              class="c_global_header__quick_links__icon indicator_icon"
              v-if="slide.icon"
              :src="slide.icon"
              :alt="slide.name + ' Icon'"
            />
            <div :class="['indicator', indicatorAnimatedClass]" :style="'--indicator-timer:' + duration + 'ms'"></div>
            <span v-if="slide.name" class="indicator_label c_text--graphik_semibold">{{ slide.name }}</span>
          </button>
          <button
            unstyle
            reset
            class="arrow-nav-btn arrow-nav-btn--mobile right-btn c_button--reset"
            @click="goToNextSlide({ userInitiated: true })"
            v-if="slideData.length > 1"
          >
            <IconSVG class="arrow-icon" handle="arrow_right" width="6" height="12" />
          </button>
          <div class="autoplay_text--mobile" v-if="slideData.length > 1">
            <Button unstyle reset @onClick="overrideAutoplay()">
              <IconSVG :handle="autoplayToggleIcon" width="12" height="12" />
            </Button>
          </div>
        </nav>
      </div>
      <!-- [END] Icon Indicator -->
    </div>
    <!--[END] Hero Banner Indicator -->
    <!--[START] Arrow Navigation -->
    <div
      v-if="(slideData.length > 1 || slideData?.[currentSlideId]?.desktopBgVideoUrl) && ['lg'].includes($mq)"
      class="c_hero_banner__navigation"
    >
      <nav class="arrow-nav" :class="{ 'arrow-nav--single': slideData.length === 1 }">
        <div class="arrow-nav-container">
          <div
            v-if="slideData?.[currentSlideId]?.desktopBgVideoUrl"
            class="pause-resume"
            @click="toggleVideoState(currentSlideId)"
          >
            <IconSVG height="8" width="8" :handle="pauseResumeIcon[currentSlideId]" aria-hidden="true" />
          </div>
          <div v-if="['lg'].includes($mq) && slideData.length > 1" class="arrow-nav-container--desktop">
            <button
              unstyle
              reset
              class="arrow-nav-btn arrow-nav-btn--desktop left-btn c_button--reset"
              @click="goToPreviousSlide({ userInitiated: true })"
            >
              <IconSVG class="arrow-icon" handle="arrow_icon_left" width="16" height="16" />
            </button>
            <button
              unstyle
              reset
              class="arrow-nav-btn arrow-nav-btn--desktop right-btn c_button--reset"
              @click="goToNextSlide({ userInitiated: true })"
            >
              <IconSVG class="arrow-icon" handle="arrow_icon_right" width="16" height="16" />
            </button>
          </div>
        </div>
        <div class="autoplay_text" v-if="slideData.length > 1">
          <Button unstyle reset @onClick="overrideAutoplay()">
            {{ autoplayToggleLabel }}
          </Button>
        </div>
      </nav>
    </div>
    <!--[END] Arrow Navigation-->
  </div>
</template>

<script>
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import MediaVideo from 'Components/video/MediaVideo.vue';
import MediaImage from 'Components/image/MediaImage.vue';
import Button from 'Components/button/Button.vue';

export default {
  data() {
    return {
      indicatorClass: '',
      indicatorAnimatedClass: '',
      currentSlideId: 0,
      slideThemeClass: 'white',
      textShadowThemeClass: '',
      indicatorIconColor: 'black',
      slideContentBoxThemeClass: '',
      slideData: [],
      desktopBannerSizingClass: 'desktop-set-height',
      mobileBannerSizingClass: 'mobile-set-height',
      stackTextImageMobile: null,
      desktopBannerAspectSizingClass: '',
      mobileBannerAspectSizingClass: '',
      slideTimer: null,
      videoId: null,
      videoSource: null,
      isVideoPlaying: {},
      autoplayOverride: false,
    };
  },
  components: {
    IconSVG,
    MediaVideo,
    MediaImage,
    Button,
  },
  props: {
    bannerSize: {
      type: Object,
      default() {
        return {
          desktopBannerHeight: '80vh',
          desktopBannerAspectRatio: 'default',
          mobileBannerHeight: '80vh',
          mobileBannerAspectRatio: 'default',
        };
      },
    },
    indicatorType: String,
    autoplay: Boolean,
    duration: Number,
    slides: Array,
  },
  methods: {
    init() {
      // Add Initial Code Here
      this.setBannerSize();
      this.setSlides();
      this.setIndicator();
      this.setTheme();
      this.setAutoplay();
      if (!this.autoplay) {
        this.overrideAutoplay({ toggle: false });
      }
    },
    setIndicator() {
      // Set's the type of indicator displayed at the bottom of the page.

      // Set Indicator Type
      switch (this.indicatorType) {
        case 'iconIndicator':
          this.indicatorClass = 'icon_indicator';
          break;
        case 'circleIndicator':
          this.indicatorClass = 'circle_indicator';
          break;
        default:
        // Do Nothing Can be expanded
      }
    },
    getCtaButtonClass(btnTYpe) {
      let btnClass = 'defaultTheme';
      switch (btnTYpe) {
        case 'slideTheme':
          btnClass = 'slideTheme';
          break;
        default:
      }
      return btnClass;
    },
    setBannerSize() {
      // Adjusts the banner size here for different aspect ratios.
      if (this.bannerSize.desktopBannerAspectRatio !== 'default') {
        // alert(this.bannerSize.desktopBannerAspectRatio);
        const desktopRatio = this.bannerSize.desktopBannerAspectRatio.split(',');
        const desktopCssClass =
          'desktop_aspect_' + desktopRatio[0].replace('.', '_') + 'x' + desktopRatio[1].replace('.', '_');
        this.desktopBannerAspectSizingClass = desktopCssClass;
        this.desktopBannerSizingClass = '';
      }
      if (this.bannerSize.mobileBannerAspectRatio !== 'default') {
        const mobileRatio = this.bannerSize.mobileBannerAspectRatio.split(',');
        const mobileCssClass =
          'mobile_aspect_' + mobileRatio[0].replace('.', '_') + 'x' + mobileRatio[1].replace('.', '_');
        this.mobileBannerAspectSizingClass = mobileCssClass;
        this.mobileBannerSizingClass = '';
      }
    },
    setSlides() {
      // Sets and Adjusts information relating to slides
      const slideArray = [];
      for (const i in this.slides) {
        const slide = {};
        slide.id = i;
        slide.name = this.slides[i].slideName;
        slide.icon = null;
        if (this.slides[i].slideIcon[0]) {
          slide.icon = this.slides[i].slideIcon[0].url;
        }
        const content = {};
        // Content Related
        content.eyebrow = this.slides[i].contentEyebrow || '';
        content.header = this.slides[i].contentHeader || '';
        content.headingTag = this.slides[i].contentHeadingTag || false;
        content.description = this.slides[i].contentDescription || '';
        content.boxEnabled = this.slides[i].contentBackgroundBoxEnabled || false;
        content.textShadowsEnabled = this.slides[i].enableTextShadows || false;
        content.textShadowOffset = this.slides[i].textShadowOffset | '';
        content.textShadowBlurStrength = this.slides[i].textShadowBlurStrength | '';

        // Call to Action Buttons
        if (this.slides[i].primaryCtaBtn) {
          const primaryBtn = {};
          primaryBtn.type = 'default';
          const ctaBtnToUrl = new URL(this.slides[i].primaryCtaBtn.url, 'https://kodak.com');
          if (ctaBtnToUrl) {
            if (ctaBtnToUrl?.hostname.includes('youtube')) {
              const btnStringToArray = ctaBtnToUrl?.search.split('=');
              primaryBtn.type = 'youtube';
              primaryBtn.videoId = btnStringToArray[1];
            }
            if (ctaBtnToUrl?.hostname.includes('youku')) {
              const btnStringToArray = ctaBtnToUrl?.pathname.split('_');
              primaryBtn.type = 'youku';
              primaryBtn.videoId = btnStringToArray[2];
            }
          }
          primaryBtn.url = this.slides[i].primaryCtaBtn.url;
          primaryBtn.label = this.slides[i].primaryCtaBtnLabel;
          primaryBtn.themeClass = this.getCtaButtonClass(this.slides[i].primaryCtaBtnTheme);
          primaryBtn.ariaLabel = this.slides[i].primaryCtaBtnAriaLabel;
          primaryBtn.target = '_self';
          if (this.slides[i].primaryCtaBtnOpenInANewWindow) {
            primaryBtn.target = '_blank';
          }
          content.primaryBtn = primaryBtn;
        }

        if (this.slides[i].secondaryCtaBtn) {
          const secondaryBtn = {};
          secondaryBtn.type = 'default';
          const secondBtnToUrl = new URL(this.slides[i].secondaryCtaBtn.url, 'https://kodak.com');
          if (secondBtnToUrl) {
            if (secondBtnToUrl?.hostname.includes('youtube')) {
              const btnStringToArray = secondBtnToUrl?.search.split('=');
              secondaryBtn.type = 'youtube';
              secondaryBtn.videoId = btnStringToArray[1];
            }
            if (secondBtnToUrl?.hostname.includes('youku')) {
              const btnStringToArray = secondBtnToUrl?.pathname.split('_');
              secondaryBtn.type = 'youku';
              secondaryBtn.videoId = btnStringToArray[2];
            }
          }
          secondaryBtn.url = this.slides[i].secondaryCtaBtn.url;
          secondaryBtn.label = this.slides[i].secondaryCtaBtnLabel;
          secondaryBtn.themeClass = this.getCtaButtonClass(this.slides[i].secondaryCtaBtnTheme);
          secondaryBtn.ariaLabel = this.slides[i].secondaryCtaBtnAriaLabel;
          secondaryBtn.target = '_self';
          if (this.slides[i].secondaryCtaBtnOpenInANewWindow) {
            secondaryBtn.target = '_blank';
          }
          content.secondaryBtn = secondaryBtn;
        }
        // Positioning Content

        // Default Positioning Values For Text Box Text
        content.desktopTextBoxWidth = '75%';
        content.desktopFlexButtonAlign = 'flex-start';
        content.mobileFlexButtonAlign = 'flex-start';
        content.desktopTextAlign = 'left';
        content.mobileTextAlign = 'left';
        content.imageDisplayClassDesktop = 'flex';
        content.imageDisplayClassMobile = 'flex';

        // Desktop Content Positions
        switch (this.slides[i].contentPositionDesktop) {
          case 'left':
            content.contentPositionDesktop = 'flex-start';
            content.desktopFlexButtonAlign = 'flex-start';
            content.desktopTextAlign = 'left';
            break;
          case 'right':
            content.contentPositionDesktop = 'flex-end';
            content.desktopFlexButtonAlign = 'flex-end';
            content.desktopTextAlign = 'right';
            break;
          case 'center':
            content.contentPositionDesktop = 'center';
            content.desktopFlexButtonAlign = 'center';
            content.desktopTextBoxWidth = '75%';
            content.desktopTextAlign = 'center';
            content.imageDisplayClassDesktop = 'none';
            break;
          default:
            content.contentPositionDesktop = 'flex-start';
            content.desktopFlexButtonAlign = 'flex-start';
            content.desktopTextAlign = 'left';
        }

        // Mobile Content Positions
        switch (this.slides[i].contentPositionMobile) {
          case 'top':
            content.mobileTextOrder = 1;
            content.mobileImageOrder = 2;
            content.contentPositionMobile = 'flex-start';
            content.mobileFlexButtonAlign = 'center';
            content.mobileTextAlign = 'center';
            break;
          case 'center':
            content.contentPositionMobile = 'center';
            content.mobileFlexButtonAlign = 'center';
            content.mobileTextAlign = 'center';
            content.imageDisplayClassMobile = 'none';

            break;
          case 'bottom':
            content.contentPositionMobile = 'flex-end';
            content.mobileTextOrder = 2;
            content.mobileImageOrder = 1;
            break;
          default:
            // Empty
            content.contentPositionMobile = 'center';
            content.mobileTextOrder = 1;
            content.mobileImageOrder = 2;
        }

        // Stack image and text
        if (this.slides[i].stackTextImageMobile) {
          content.stackTextImageMobile = 'stack-items';
        }

        // Overall Theming
        slide.theme = this.slides[i].colorTheme;
        slide.overlayOpacity = this.slides[i].backgroundOverlayOpacity;
        // Background Imagery
        if (this.slides[i].backgroundImageDesktop[0] && this.bannerSize.desktopBannerAspectRatio === 'default') {
          slide.desktopBgImageUrl =
            this.slides[i].backgroundImageDesktop[0].lpBackgroundWide?.src ||
            this.slides[i].backgroundImageDesktop.url ||
            null;
          if (slide.desktopBgImageUrl === '' || !slide.desktopBgImageUrl) {
            slide.desktopBgImageUrl =
              this.slides[i].backgroundImageDesktop[0].uncropped1920w?.src ||
              this.slides[i].backgroundImageDesktop.url ||
              null;
          }
        } else if (this.slides[i].backgroundImageDesktop[0] && this.bannerSize.desktopBannerAspectRatio !== 'default') {
          slide.desktopBgImageUrl =
            this.slides[i].backgroundImageDesktop[0].uncropped1920w?.src ||
            this.slides[i].backgroundImageDesktop.url ||
            null;
        } else {
          slide.desktopBgImageUrl = null;
        }
        // slide.desktopBgImageUrl = this.slides[i].backgroundImageDesktop[0].uncropped1920w?.src
        if (this.slides[i].backgroundVideo[0]) {
          slide.desktopBgVideoUrl = this.slides[i].backgroundVideo[0].url;
        } else {
          slide.desktopBgVideoUrl = null;
        }
        //BG Mobile Logic
        if (this.slides[i].backgroundImageMobile?.[0]) {
          slide.mobileBgImageUrl = this.slides[i].backgroundImageMobile[0].lpImageTextUncropped.src || null;
          slide.mobileBgImageSrcset = this.slides[i].backgroundImageMobile[0].lpImageTextUncropped;
        } else {
          slide.mobileBgImageUrl = slide.mobileBgImageSrcset = null;
        }
        if (this.slides[i].backgroundVideoMobile?.[0]) {
          slide.mobileBgVideoUrl = this.slides[i].backgroundVideoMobile[0].url;
        } else {
          slide.mobileBgVideoUrl = null;
        }
        // Loop Background Video boolean
        content.loopBackgroundVideo = this.slides[i].loopBackgroundVideo ?? true;
        content.loopBackgroundVideoMobile = this.slides[i].loopBackgroundVideoMobile ?? true;
        slide.content = content;

        slideArray.push(slide);
      }
      this.slideData = slideArray;
      this.isVideoPlaying = slideArray.reduce((acc, _, key) => {
        acc[key] = true;
        return acc;
      }, {});
    },
    setTheme() {
      // Set's Colors within the slide.
      if (this.slideData.length > 0) {
        this.slideThemeClass = this.slideData[this.currentSlideId].theme;
        if (this.slideData[this.currentSlideId].content.boxEnabled) {
          // Show Bounding Box Around Text
          this.slideContentBoxThemeClass = this.slideData[this.currentSlideId].theme;
        } else if (
          this.slideThemeClass === 'black' &&
          this.slideData[this.currentSlideId].content.stackTextImageMobile === 'stack-items'
        ) {
          this.slideContentBoxThemeClass =
            'transparent-box-white-text transparent-box-white-text--stacked black black--mobile ';
        } else if (
          this.slideThemeClass === 'darkGray' &&
          this.slideData[this.currentSlideId].content.stackTextImageMobile === 'stack-items'
        ) {
          this.slideContentBoxThemeClass =
            'transparent-box-white-text transparent-box-white-text--stacked darkGray darkGray--mobile ';
        } else if (
          this.slideThemeClass === 'gray' &&
          this.slideData[this.currentSlideId].content.stackTextImageMobile === 'stack-items'
        ) {
          this.slideContentBoxThemeClass =
            'transparent-box-black-text transparent-box-black-text--stacked gray gray--mobile ';
        } else if (
          this.slideThemeClass === 'yellow' &&
          this.slideData[this.currentSlideId].content.stackTextImageMobile === 'stack-items'
        ) {
          this.slideContentBoxThemeClass =
            'transparent-box-black-text transparent-box-black-text--stacked yellow yellow--mobile ';
        } else if (
          this.slideThemeClass === 'white' &&
          this.slideData[this.currentSlideId].content.stackTextImageMobile === 'stack-items'
        ) {
          this.slideContentBoxThemeClass =
            'transparent-box-black-text transparent-box-black-text--stacked white white--mobile';
        } else if (this.slideThemeClass === 'black' || this.slideThemeClass === 'darkGray') {
          // If box is disabled and background is black or darkGray, make the box transparent and set text to white
          this.slideContentBoxThemeClass = 'transparent-box-white-text transparent-box-white-text--stacked';
        } else {
          // If box is disabled and background is gray, yellow, or white, make the box transparent and set text to black
          this.slideContentBoxThemeClass = 'transparent-box';
        }
        if (this.slideThemeClass === 'black' || this.slideThemeClass === 'darkGray') {
          this.indicatorIconColor = 'white';
        } else {
          this.indicatorIconColor = 'black';
        }
        if (this.slideData[this.currentSlideId].content.textShadowsEnabled) {
          this.textShadowThemeClass = 'text-shadow';
        } else {
          this.textShadowThemeClass = '';
        }
      }
    },
    goToPreviousSlide({ userInitiated = false } = {}) {
      // Go Back a Slide
      if (this.currentSlideId === 0) {
        this.currentSlideId = this.slideData.length - 1;
      } else {
        this.currentSlideId = this.currentSlideId - 1;
      }
      if (userInitiated) {
        this.overrideAutoplay({ toggle: false });
      }
      this.setTheme();
    },
    goToNextSlide({ userInitiated = false } = {}) {
      // Go to Next Slide
      if (this.currentSlideId === this.slideData.length - 1) {
        this.currentSlideId = 0;
      } else {
        this.currentSlideId = this.currentSlideId + 1;
      }
      if (userInitiated) {
        this.overrideAutoplay({ toggle: false });
      }
      this.setTheme();
    },
    goToSlide(slideId) {
      // Go to Specific Slide
      this.currentSlideId = slideId;
      this.setTheme();
      // Disable autoplay after a user has interacted with slider nav
      this.overrideAutoplay({ toggle: false });
    },
    pauseAutoplay() {
      // Pause the Autoplay
      if (this.checkIfAutoplayEnabled()) {
        clearTimeout(this.slideTimer);
        this.indicatorAnimatedClass = 'non-animated';
      }
    },
    executeAutoplay() {
      clearTimeout(this.slideTimer);
      if (!this.autoplayOverride) {
        // Execute Play
        const vue = this;
        this.slideTimer = setTimeout(function () {
          vue.goToNextSlide();
          vue.executeAutoplay('self');
        }, this.duration);
      }
    },
    setAutoplay() {
      // Check if Autoplay is Enabled
      if (this.checkIfAutoplayEnabled() && !this.autoplayOverride) {
        const vue = this;
        this.indicatorAnimatedClass = 'animated';
        this.slideTimer = setTimeout(function () {
          vue.goToNextSlide();
          vue.executeAutoplay();
        }, this.duration);
      } else {
        this.indicatorAnimatedClass = '';
      }
    },
    checkIfAutoplayEnabled() {
      let autoplayEnabled = false;
      if (this.autoplay) {
        // Check if More than 1 Slide
        if (this.slideData.length > 0) {
          autoplayEnabled = true;
        }
      }
      return autoplayEnabled;
    },
    toggleVideoState(key) {
      const videoElement = this.$refs[`slideVideo${key}`]?.[0];
      if (videoElement) {
        if (this.isVideoPlaying[key]) {
          videoElement.pause();
          this.isVideoPlaying[key] = false;
        } else {
          videoElement.play();
          this.isVideoPlaying[key] = true;
        }
      }
    },
    //Videos that don't loop need to be marked as not playing once they end
    onVideoEnd(key) {
      this.isVideoPlaying[key] = false;
    },
    overrideAutoplay({ toggle = true } = {}) {
      clearTimeout(this.slideTimer);

      if (!toggle) {
        this.autoplayOverride = true;
        return this.pauseAutoplay();
      }

      this.autoplayOverride = !this.autoplayOverride;
      return this.autoplayOverride ? this.pauseAutoplay() : this.executeAutoplay();
    },
  },
  created() {},
  mounted() {
    this.init();
  },
  computed: {
    pauseResumeIcon() {
      return Object.keys(this.isVideoPlaying).reduce((acc, key) => {
        acc[key] = this.isVideoPlaying[key] ? 'hotspot_pause' : 'hotspot_play';
        return acc;
      }, {});
    },
    autoplayToggleLabel() {
      const autoplayTranslation = this.$t('Autoplay');
      return this.autoplayOverride
        ? `${autoplayTranslation} ${this.$t('Off')}`
        : `${autoplayTranslation} ${this.$t('On')}`;
    },
    autoplayToggleIcon() {
      return this.autoplayOverride ? 'hotspot_play' : 'hotspot_pause';
    },
  },
};
</script>

<style lang="scss">
@mixin absolute_fill() {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0px;
  left: 0px;
}
@mixin transition($transition) {
  -webkit-transition: $transition;
  -moz-transition: $transition;
  -o-transition: $transition;
  -ms-transition: $transition;
  transition: $transition;
}
@mixin aspect-ratio($width, $height) {
  position: relative;
  &:before {
    display: block;
    content: '';
    // width: 100%;
    padding-top: ($height / $width) * 100%;
  }
  > .content {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}

.pause-resume {
  position: relative;
  z-index: 10;
  cursor: pointer;
  @include transition(all 0.1s ease);
  display: none;
  align-items: center;
  justify-content: center;
  height: px_to_rem(40);
  width: px_to_rem(40);
  border-radius: 50%;
  background-color: $color_black;
  border: 1px solid $color_black;
  border-color: $color_white;
  color: $color_white;
  &:hover {
    background-color: lighten(#393939, 3%);
  }
  .c_icon_svg {
    align-items: center;
    display: flex;
  }
  @include mq($mq_md_to_lg) {
    display: flex;
  }
}
.pause-resume-mobile {
  display: flex;
  position: absolute;
  bottom: px_to_rem(10);
  right: px_to_rem(10);
  width: px_to_rem(28);
  height: px_to_rem(28);
  @include mq($mq_md_to_lg) {
    display: none;
  }
}
.c_hero_banner {
  $self: &;
  position: relative;
  @at-root #{$self}__overlay {
    background: rgba($color_black_raw, 0.5);

    @at-root #{&}__video {
      width: 90vw;
      max-width: 100%;
      overflow: hidden;
      @include mq($mq_sm_to_md) {
        width: 70vw;
      }
      @include mq($mq_md_to_lg) {
        width: px_to_rem(800);
      }
    }
  }

  position: relative;
  width: 100%;
  overflow-y: hidden;
  /* Theme Backgrounds */
  .white {
    background-color: white;
  }
  .black {
    background-color: black;
    color: $color_white;
  }
  .yellow {
    background-color: $color_yellow;
  }
  .gray {
    background-color: $color_gray_201;
  }
  .darkGray {
    background-color: $color_gray_41;
    color: $color_white;
  }
  .transparent-box {
    background-color: transparent;
  }
  .transparent-box-white-text {
    background-color: transparent;
    color: $color_white;
  }

  @at-root #{$self}__slide {
    position: relative;
    overflow-x: hidden;
    .sizing-box {
      display: grid;
      @include transition(all 0.25s ease);
    }
    // Stack items (mobile only)
    @media only screen and (max-width: ($mq_md_to_lg + 'px')) {
      .sizing-box.stack-items {
        display: flex;
        flex-direction: column;
        width: 100%;
      }
    }
  }

  @include mq($mq_md_to_lg) {
    /* Desktop Only Classes*/
    .sizing-box.desktop-set-height {
      height: var(--desktop-banner-height);
    }
    // Opportunity to reduce code
    .desktop_aspect_16x9 {
      @include aspect-ratio(16, 9);
    }
    .desktop_aspect_1_85x1 {
      @include aspect-ratio(1.85, 1);
    }
    .desktop_aspect_2_35x1 {
      @include aspect-ratio(2.35, 1);
    }
    .desktop_aspect_4x1 {
      @include aspect-ratio(4, 1);
    }
    .desktop_aspect_5x1 {
      @include aspect-ratio(5, 1);
    }
  }
  @media only screen and (max-width: 1024px) {
    /* Mobile Only Classes */
    @at-root #{$self}__slide {
      .sizing-box.mobile-set-height {
        height: var(--mobile-banner-height);
      }
    }
    // Opportunity to reduce code
    .mobile_aspect_16x9 {
      @include aspect-ratio(16, 9);
    }
    .mobile_aspect_9x16 {
      @include aspect-ratio(9, 16);
    }
    .mobile_aspect_1_85x1 {
      @include aspect-ratio(1.85, 1);
    }
    .mobile_aspect_1x1_85 {
      @include aspect-ratio(1, 1.85);
    }
    .mobile_aspect_2_35x1 {
      @include aspect-ratio(2.35, 1);
    }
    .mobile_aspect_1x2_35 {
      @include aspect-ratio(1, 2.35);
    }
  }
  .desktop {
    // Hide for Mobile
    display: none;
    @include mq($mq_md_to_lg) {
      // Show for Desktop
      display: flex;
    }
  }
  .mobile {
    // Show for Mobile
    @include mq($mq_md_to_lg + 1) {
      // Hide for Mobile
      display: none;
    }
  }
  /* Background Overlay */
  /* Video BG: https://www.w3schools.com/howto/howto_css_fullscreen_video.asp */
  .c_hero_banner__slide {
    display: none;
  }
  .c_hero_banner__slide.active {
    display: flex;
  }
  .aspect_ratio_stack_set {
    .c_hero_banner__content {
      display: none;
    }
    .c_hero_banner__content.active {
      display: flex;
    }
  }
  @at-root #{$self}__background {
    $background: &;
    order: var(--mobile-image-order);
    @include absolute_fill();
    // Stack items (mobile only)
    @at-root #{$self}__background.stack-items {
      @media only screen and (max-width: ($mq_md_to_lg + 'px')) {
        position: relative;
      }
    }
    @at-root #{$background} video {
      object-fit: cover;
    }
    @at-root #{$background}__overlay {
      @include absolute_fill();
      background-color: black;
      opacity: var(--opacity);
      z-index: 2;
    }
    @at-root #{$background}__overlay.light {
      background-color: white;
    }
    @at-root #{$background}__visual {
      $visual: &;
      @include absolute_fill();
      background-color: transparent;
      background-color: $color_black;
      z-index: 1;
      background-size: cover;
      background-position: center;
      overflow-x: hidden;
      overflow-y: hidden;
      /*c_hero_banner__background__visual__video{*/
      @at-root #{$visual}__video {
        position: absolute;
        top: 50%;
        left: 50%;
        min-width: 100%;
        min-height: 100%;
        // width: auto;
        // height: auto;
        z-index: -100;
        -ms-transform: translateX(-50%) translateY(-50%);
        -moz-transform: translateX(-50%) translateY(-50%);
        -webkit-transform: translateX(-50%) translateY(-50%);
        transform: translateX(-50%) translateY(-50%);
        background-size: cover;
        // overflow-x: hidden;
      }
    }
  }
  @at-root #{$self}__content {
    $content: &;
    position: absolute;
    top: 0px;
    width: 100%;
    height: 100%;
    bottom: 50px;
    display: flex;
    left: 0px;
    z-index: 3;
    justify-content: center;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    align-content: stretch;
    /*.c_wrapper {*/
    .content_wrapper {
      display: flex;
      overflow-y: hidden;
      align-items: stretch; /* might need to modify*/
      align-content: stretch;
      flex-direction: row;
      grid-template-areas:
        'content-area'
        'image-area';
      margin-left: auto;
      margin-right: auto;
      width: var(--max-width); /*1024px;*/ /* Grid Width*/
      height: 100%;
      .content-container-box {
        grid-area: content-area;
        display: flex;
        justify-content: var(--desktop-content-position);
        text-align: var(--desktop-text-align);
        align-items: center;
        align-content: center;
        width: 100%;
        .content-box {
          display: flex;
          padding: px_to_rem(25); /* Make Editable */
          flex-direction: column;
          max-width: px_to_rem(550);
          width: 100%;
          .cta_container {
            flex-direction: row;
            flex-wrap: wrap;
            display: flex;
            gap: px_to_rem(16);
            justify-content: var(--desktop-justify-align);
            @include mq($mq_sm_to_md) {
              flex-direction: row;
            }
            button,
            a {
              margin: 0;
              width: auto;
              border-width: 2px;
              @include transition(all 0.25s ease);
            }
          }
          /* Margins are not added when the page does not include other widgets */
          .c_lp_text__eyebrow {
            @include font('graphik_semibold');
            margin-bottom: var(--eyebrow-mb, 1.2rem);
          }
          .c_lp_text__subheader {
            margin-bottom: var(--subheader-mb, 0.85rem);
          }
          .c_lp_text__text {
            margin-bottom: var(--text-mb, 1.5rem);
          }
        }
        .content-box.light {
          color: $color_white;
        }
        .content-box.text-shadow {
          text-shadow: var(--shadow-offset) var(--shadow-offset) var(--shadow-blur) $color_black;
          h1 {
            text-shadow: var(--shadow-offset) var(--shadow-offset) var(--shadow-blur) $color_black;
          }
        }
      }
    }
  }
  @media only screen and (min-width: 1024px) and (max-width: 1180px) {
    @at-root #{$self}__content {
      .content_wrapper {
        width: auto;
        max-width: 100%;
        margin-left: 30px;
        margin-right: 30px;
      }
    }
  }
  @media only screen and (max-width: 1024px) {
    /*mq_md_to_lg*/
    @at-root #{$self}__content {
      bottom: 0px;
      flex-direction: row;
      order: var(--mobile-text-order);
      /*.c_wrapper {*/
      .content_wrapper {
        display: flex;
        justify-content: var(--mobile-content-position);
        align-items: stretch; /* might need to modify*/
        align-content: var(--mobile-content-position);
        flex-direction: column;
        .content-container-box {
          display: flex;
          max-width: 100%;
          width: auto;
          text-align: var(--mobile-text-align);
          justify-content: center;
          align-items: center;
          align-content: center;
          flex-direction: row;
          .content-box {
            .cta_container {
              justify-content: var(--mobile-justify-align);
            }
          }
        }
      }
    }
  }
  // Stack items (mobile only)
  @media only screen and (max-width: ($mq_md_to_lg + 'px')) {
    @at-root #{$self}__content.stack-items {
      position: relative;
      .content-container-box {
        width: 100%;
        height: 100%;
        .content-box {
          justify-content: center;
          width: 100%;
          max-width: 100%;
          height: 100%;
        }
      }
    }
  }
  @at-root #{$self}__indicator {
    $indicator: &;
    position: static;
    bottom: 0px;
    width: 100%;
    left: 0px;
    z-index: 4;
    @include mq($mq_md_to_lg) {
      position: absolute;
    }
    @at-root #{$self}__indicator__menu__container {
      height: 45px;
      width: 100%;
      /*@at-root #{$indicator}__menu {*/
      .c_hero_banner__indicator__menu {
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: px_to_rem(8);
        .indicator_btn {
          height: 100%;

          flex-grow: 0;
          flex-basis: px_to_rem(20);
          .indicator {
            position: relative;
            @include transition(all 0.25s ease);
            background-color: #555555;
            opacity: 1;
            height: 5px;
          }
          .indicator_label {
            display: none;
          }
          .indicator_icon {
            display: none;
          }
          @include mq($mq_md_to_lg) {
            flex-grow: 1;
            flex-basis: 100%;
          }
        }
        .indicator_btn:hover {
          .indicator {
            background-color: rgba(255, 255, 255, 0.6);
          }
        }
        .indicator_btn.active {
          .indicator {
            background-color: $color_white;
            opacity: 1;
            // &:after {
            //   content: '';
            //   position: absolute;
            //   animation: indicatorFill var(--indicator-timer) linear forwards;
            //   top: 0;
            //   left: 0;
            //   width: 0%;
            //   height: 5px;
            //   background-color: $color_yellow;
            //   z-index: 5;
            // }
          }
        }
        // @keyframes indicatorFill {
        //   0% {
        //     width: 0%;
        //   }
        //   100% {
        //     width: 100%;
        //   }
        // }
      }
      .c_hero_banner__indicator__menu.light {
        /* Note: Dark is default*/
        .indicator_btn {
          .indicator {
            background-color: rgba(255, 255, 255, 0.5);
          }
        }
      }
      //mobile slider constraints to prevent overflow
      @media (max-width: 1024px) {
        background-color: $color_black;
        .c_wrapper {
          height: 100%;
          .c_hero_banner__indicator__menu {
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            .indicator_btn {
              margin-right: 7px;
              min-width: 10px;
              max-width: 20px;
              flex: 1 1 20px;
              color: white;
              .indicator {
                background-color: $color_white;
              }
            }
          }
        }
      }
    }
    @at-root #{$self}__indicator__menu__container.circle_indicator {
      .c_hero_banner__indicator__menu {
        display: flex;
        justify-content: center;
        .indicator_btn {
          margin-right: 10px;
          .indicator {
            width: 10px;
            height: 10px;
            border-radius: 20px;
          }
        }
      }
    }
    @at-root #{$self}__indicator__menu__container.icon_indicator {
      /*Statically Set to Default Indicators*/
      background-color: $color_black;
      .c_hero_banner__indicator__menu {
        display: flex;
        justify-content: center;
        .indicator_btn {
          .indicator {
            background-color: $color_white;
          }
        }
      }
    }
    @include mq($mq_md_to_lg) {
      /* The Icon Nav is only on Desktop Displays */
      @at-root #{$self}__indicator__menu__container.icon_indicator {
        /* Indicator Menu has icons*/
        background-color: $color_black;
        .c_hero_banner__indicator__menu {
          display: flex;
          justify-content: space-between;
          align-content: stretch;
          align-items: stretch;
          position: relative;
          .indicator_btn {
            width: 100%;
            display: flex;
            position: relative;
            align-items: center;
            justify-content: flex-start;
            .indicator {
              position: absolute;
              display: inline-block;
              top: 0px;
              height: 3px;
              background-color: var(--color-yellow);
              width: 100%;
              opacity: 0;
            }
            .indicator_label {
              display: inline-flex;
              padding-left: 5px;
              padding-right: 5px;
              font-size: 14px;
              color: $color_white;
            }
            .indicator_icon {
              display: inline-flex;
              margin-right: 10px;
              margin-left: 10px;
              width: 30px;
              height: 30px;
            }
          }
          .indicator_btn:hover {
            .indicator {
              background-color: var(--color-yellow);
              opacity: 1;
            }
          }
          .indicator_btn.active {
            .indicator {
              opacity: 1;
            }
            .indicator.animated {
              width: 0px;
              animation-name: indicator-animation;
              animation-duration: var(--indicator-timer);
              animation-iteration-count: infinite;
              animation-timing-function: linear;
              animation-iteration-count: 1;
            }
            .indicator.non-animated {
              width: 100%;
              animation-name: indicator-non-animation;
              animation-duration: 300ms;
              animation-timing-function: ease-in;
              animation-iteration-count: 1;
            }
            @keyframes indicator-non-animation {
              100% {
                width: 100%;
              }
            }
            @keyframes indicator-animation {
              0% {
                width: 0px;
              }
              100% {
                width: 100%;
              }
            }
          }
        }
      }
    }
  }
  .c_hero_banner__navigation {
    $navigation: &;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    .arrow-nav {
      height: 100%;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      flex-direction: column;
      padding-right: px_to_rem(24);
      padding-bottom: px_to_rem(50);
      gap: px_to_rem(8);
      bottom: 0;
      right: 0;
      text-align: right;
      &:after {
        pointer-events: none;
        content: none;
        position: absolute;
        bottom: 0;
        left: 0;
        height: px_to_rem(584);
        width: 100%;
        background: linear-gradient(
          to bottom,
          rgba(0, 0, 0, 0) 0%,
          rgba(0, 0, 0, 0) 55%,
          rgba(0, 0, 0, 0.08) 62%,
          rgba(0, 0, 0, 0.27) 75%,
          rgba(0, 0, 0, 0.62) 87%,
          rgba(0, 0, 0, 1) 97%,
          rgba(0, 0, 0, 1) 100%
        );
        z-index: 1;
        @include mq($mq_md_to_lg) {
          content: '';
        }
      }
      &--single {
        padding-bottom: px_to_rem(24);
        &:after {
          content: none;
        }
      }
    }
    .arrow-nav-container {
      display: flex;
      gap: px_to_rem(24);
      .arrow-nav-container--desktop {
        display: flex;
        gap: px_to_rem(12);
      }
    }
  }
  .autoplay_text {
    z-index: 10;
    margin-right: px_to_rem(8);
    button {
      text-decoration: underline;
      color: $color_white;
      font-size: px_to_rem(12);
      &:hover {
        text-decoration: none;
      }
    }
    &--mobile {
      display: block;
      position: absolute;
      right: px_to_rem(0);
      bottom: px_to_rem(0);
      color: $color_white;
      button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: px_to_rem(45);
        height: px_to_rem(45);
      }
      @include mq($mq_md_to_lg) {
        display: none;
      }
    }
  }
  .arrow-nav-btn {
    @include transition(all 0.1s ease);
    display: flex;
    height: 40px;
    width: 40px;
    justify-content: center;
    align-items: center;
    z-index: 9;
    border-radius: 50%;
    background-color: $color_black;
    border: 1px solid $color_black;
    border-color: $color_white;
    color: $color_white;
    &:hover {
      background-color: lighten(#393939, 3%);
    }
    &--mobile {
      display: flex;
      background-color: transparent;
      border: none;
      @include mq($mq_md_to_lg) {
        display: none;
      }
    }
  }
  .transparent-box {
    .c_button_2022__secondary {
      color: $color_white;
      border-color: $color_white;
      &:hover {
        background-color: $color_white;
        color: $color_black;
      }
    }
  }
  // Potentially don't need this. Commenting out fixes #1103
  // .stack-items:not(.content-box) {
  //   &.yellow,
  //   &.darkGray,
  //   &.gray,
  //   &.black,
  //   &.white {
  //     @media (min-width: ($mq_md_to_lg + 'px')) {
  //       color: $color_white;
  //       .c_button_2022--primary {
  //         background-color: $color_white;
  //         color: $color_black;
  //         border-color: $color_white;
  //         &:hover {
  //           background-color: transparent;
  //           color: $color_white;
  //         }
  //       }
  //       .c_button_2022__secondary {
  //         background-color: transparent;
  //         border-color: $color_white;
  //         color: $color_white;
  //         &:hover {
  //           color: $color_black;
  //           background-color: $color_white;
  //         }
  //       }
  //     }
  //   }
  // }
  .yellow {
    .c_button_2022--primary {
      background-color: $color_black;
      color: $color_white;
      border-color: $color_black;
      &:hover {
        background-color: transparent;
        color: $color_black;
      }
    }
    .c_button_2022__secondary {
      background-color: transparent;
      border-color: $color_black;
      color: $color_black;
      &:hover {
        color: $color_white;
        background-color: $color_black;
      }
    }
    &--mobile {
      background-color: $color_yellow;
      @include mq($mq_md_to_lg) {
        background-color: transparent;
      }
    }
  }
  .black {
    .c_button_2022__secondary {
      background-color: transparent;
      border-color: $color_white;
      color: $color_white;
      &:hover {
        background-color: $color_white;
        color: $color_black;
      }
    }
    &--mobile {
      background-color: $color_black;
      @include mq($mq_md_to_lg) {
        background-color: transparent;
      }
    }
  }
  .gray {
    .c_button_2022__secondary {
      border-color: $color_black;
      color: $color_black;
      background-color: transparent;
      &:hover {
        background-color: $color_black;
        color: $color_white;
      }
    }
    &--mobile {
      background-color: $color_gray_201;
      @include mq($mq_md_to_lg) {
        background-color: transparent;
      }
    }
  }
  .white {
    .c_button_2022__secondary {
      color: $color_red;
      &:hover {
        color: $color_white;
      }
    }
    &--mobile {
      background-color: $color_white;
      @include mq($mq_md_to_lg) {
        background-color: transparent;
      }
    }
  }
  .darkGray {
    .c_button_2022__secondary {
      border-color: $color_white;
      &:hover {
        background-color: $color_white;
        color: $color_black;
      }
    }
    &--mobile {
      background-color: $color_gray_41;
      @include mq($mq_md_to_lg) {
        background-color: transparent;
      }
    }
  }
}
</style>
